import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { analyticsInterceptorV2 } from '../utils/Axios/analyticsInterceptorv2';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Student Intelligence rankings: every learner of a cohort scored and
// ranked, overall and per module (analytics service;
// lms_backend docs/api/student-intelligence.md).
const BASE = '/student-intelligence';
const KEY = 'student-intelligence';
export const RANKING_PAGE_SIZE = 50;
const COHORTS_LIMIT = 500;

const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

const options = { refetchOnWindowFocus: false, retry: false };

// Every cohort for the picker. /cohorts pages by $limit; same key as the
// Courses page so they share the cache.
export const useAllCohorts = () =>
  useQuery(
    ['cohorts', 'all', COHORTS_LIMIT],
    () =>
      axiosInstancev2.get('/cohorts', { params: { $limit: COHORTS_LIMIT } }),
    { ...options, staleTime: 60 * 60 * 1000 }
  );

export const useRanking = (cohortId, filters, offset = 0) => {
  const params = clean({
    module_id: filters.moduleId,
    sort: filters.sort === 'score' ? undefined : filters.sort,
    q: filters.q,
    limit: RANKING_PAGE_SIZE,
    offset,
  });
  return useQuery(
    [KEY, cohortId, 'ranking', params],
    async () =>
      (
        await analyticsInterceptorV2.get(`${BASE}/cohorts/${cohortId}`, {
          params,
        })
      ).data,
    { ...options, enabled: Boolean(cohortId), keepPreviousData: true }
  );
};

export const useModuleRankings = (cohortId) =>
  useQuery(
    [KEY, cohortId, 'modules'],
    async () =>
      (await analyticsInterceptorV2.get(`${BASE}/cohorts/${cohortId}/modules`))
        .data,
    { ...options, enabled: Boolean(cohortId) }
  );

export const useLearnerRanking = (studentId, cohortId) =>
  useQuery(
    [KEY, cohortId || 'primary', 'student', studentId],
    async () =>
      (
        await analyticsInterceptorV2.get(`${BASE}/students/${studentId}`, {
          params: clean({ cohort_id: cohortId }),
        })
      ).data,
    { ...options, enabled: Boolean(studentId) }
  );

export const useWeights = (cohortId) =>
  useQuery(
    [KEY, cohortId, 'weights'],
    async () =>
      (await analyticsInterceptorV2.get(`${BASE}/cohorts/${cohortId}/weights`))
        .data,
    { ...options, enabled: Boolean(cohortId) }
  );

// Saving weights recomputes the cohort, so everything for it is stale.
export const useSaveWeights = (cohortId) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (weights) =>
      (
        await analyticsInterceptorV2.put(
          `${BASE}/cohorts/${cohortId}/weights`,
          {
            weights,
          }
        )
      ).data,
    { onSuccess: () => queryClient.invalidateQueries([KEY, cohortId]) }
  );
};

// Rankings are cached for 10 minutes on the server; this recomputes them.
export const useRecompute = (cohortId) => {
  const queryClient = useQueryClient();
  return useMutation(
    () =>
      analyticsInterceptorV2.get(`${BASE}/cohorts/${cohortId}`, {
        params: { refresh: 'true', limit: 1 },
      }),
    { onSuccess: () => queryClient.invalidateQueries([KEY, cohortId]) }
  );
};

export const exportPath = (cohortId) => `${BASE}/cohorts/${cohortId}/export`;

export const siUnavailable = (error) => {
  const data = error?.response?.data;
  return error?.response?.status === 404 && !(data?.message || data?.error);
};

export const siError = (error, fallback) => {
  if (siUnavailable(error)) {
    return "Rankings aren't available on the server yet.";
  }
  const data = error?.response?.data;
  return data?.message || data?.error || fallback;
};
